/**
 * WorkoutRoutineForm Component (Refactored)
 * Main form component using React Hook Form + Zod
 * Requirements: 1.1, 1.2, 1.4
 */

import * as React from 'react';
import {
  memo,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  FormEvent,
} from 'react';
import {FormProvider} from 'react-hook-form';
import styled from 'styled-components';

import {TabsHeader} from '../../../components';
import {LoadingComponent} from '../../../components/LoadingComponent';
import Breadcrumb from '../../../components/Breadcrumb';
import {Card} from '../../../elements';

import {useWorkoutRoutineForm} from './WorkoutRoutineForm.use-case';
import {
  saveWorkoutRoutine,
  saveAllWorkoutGroups,
} from './WorkoutRoutineForm.service';
import {transformFromResponse} from './WorkoutRoutineForm.map';
import {WorkoutGroupField} from './_components/WorkoutGroupField/WorkoutGroupField';
import {ConfigTab} from './_components/ConfigTab/ConfigTab';
import {resolveErrorTab} from './WorkoutRoutineForm.tabs';

import type {
  WorkoutRoutineFormProps,
  FormSubmitResult,
} from './WorkoutRoutineForm.type';
import type {WorkoutRoutineFormData} from './WorkoutRoutineForm.schema';
import {TabTypes} from '../../../types';

// Styled components
const RoutineFormContainer = styled.div`
  padding: 0;
`;

const WorkoutGroupsContainer = styled.div`
  margin-top: 20px;
`;

const AddWorkoutGroupButton = styled.div`
  background: #f5f5f5;
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.2s ease;

  &:hover {
    background: #e8e8e8;
    border-color: #999;
  }
`;

const SubmitButtonContainer = styled.div`
  margin-top: 20px;
  padding: 20px 0;
`;

// Tab configuration
const TabsList = [
  {
    icon: 'cog',
    label: 'Configurações do treino',
    value: 'config',
  },
  {
    icon: 'pen',
    label: 'Edição do treino',
    value: 'edit',
  },
];

/**
 * WorkoutRoutineForm Component
 * Main form component that manages the workout routine form state
 * Uses React Hook Form for state management and Zod for validation
 */
export const WorkoutRoutineForm = memo(function WorkoutRoutineForm({
  daysWeek,
  users,
  path,
  programs,
  tabActive: initialTabActive,
  workoutRoutine: initialWorkoutRoutine,
  exerciseListDefault = [],
}: WorkoutRoutineFormProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<TabTypes>(
    initialTabActive || TabTypes.config,
  );
  const formRef = useRef<ReturnType<typeof useWorkoutRoutineForm>['form'] | null>(null);

  const initialData = useMemo((): Partial<WorkoutRoutineFormData> | undefined => {
    if (!initialWorkoutRoutine) {
      return undefined;
    }
    
    if ('workoutGroups' in initialWorkoutRoutine && Array.isArray(initialWorkoutRoutine.workoutGroups)) {
      return initialWorkoutRoutine as WorkoutRoutineFormData;
    }
    
    return transformFromResponse(initialWorkoutRoutine as any);
  }, [initialWorkoutRoutine]);

  const handleSubmit = useCallback(
    async (data: WorkoutRoutineFormData): Promise<FormSubmitResult> => {
      const routineResult = await saveWorkoutRoutine(data, {
        showNotifications: true,
      });

      if (!routineResult.success) {
        return {
          success: false,
          errors: routineResult.errors,
        };
      }

      const routineId = routineResult.data?.id || data.id;
      
      if (routineId && formRef.current) {
        formRef.current.setValue('id', routineId);
      }
      
      if (routineId && data.workoutGroups && data.workoutGroups.length > 0) {
        const groupsResult = await saveAllWorkoutGroups(
          routineId,
          data.workoutGroups,
          {showNotifications: true},
        );

        if (!groupsResult.success) {
          return {
            success: false,
            errors: groupsResult.errors,
          };
        }

        console.log('handle submit =======>', data);

        if (groupsResult.indexMap && formRef.current) {
          const currentGroups = formRef.current.getValues('workoutGroups');
          const updatedGroups = currentGroups.map((group, index) => {
            const savedGroup = groupsResult.indexMap?.get(index);
            if (savedGroup) {
              return {
                ...group,
                id: savedGroup.id,
                workouts: group.workouts?.map((workout, wIndex) => {
                  const savedWorkout = savedGroup.workouts?.[wIndex];
                  if (savedWorkout) {
                    return {
                      ...workout,
                      id: savedWorkout.id,
                      exerciseWorkouts: workout.exerciseWorkouts?.map((exercise, eIndex) => {
                        const savedExercise = savedWorkout.exerciseWorkouts?.[eIndex];
                        if (savedExercise) {
                          return {
                            ...exercise,
                            id: savedExercise.id,
                          };
                        }
                        return exercise;
                      }),
                    };
                  }
                  return workout;
                }),
              };
            }
            return group;
          });
          formRef.current.setValue('workoutGroups', updatedGroups, {shouldValidate: false});
        }
      }

      if (path === 'new' && routineId) {
        const currentPath = window.location.pathname;
        const accountMatch = currentPath.match(/^\/([^/]+)\/admin/);
        const accountPrefix = accountMatch ? `/${accountMatch[1]}` : '';
        window.history.pushState(
          {},
          '',
          `${accountPrefix}/admin/workout-routines/${routineId}/edit`,
        );
      }

      return {
        success: true,
        data: routineResult.data,
      };
    },
    [path],
  );

  const {
    form,
    workoutGroupsField,
    isLoading,
    progress,
    errors,
    addWorkoutGroup,
    removeWorkoutGroup,
    moveWorkoutGroup,
    submitForm,
  } = useWorkoutRoutineForm({
    initialData,
    path,
    onSubmit: handleSubmit,
  });

  formRef.current = form;

  useEffect(() => {
    if (initialTabActive) {
      setActiveTab(initialTabActive);
    }
  }, [initialTabActive]);

  // Handle tab change
  const onChangeTab = useCallback((tabValue: string) => {
    setActiveTab(tabValue as TabTypes);
  }, []);

  // Switch to the tab that holds the validation error so it becomes visible
  const focusErrorTab = useCallback((errors?: Record<string, string>) => {
    const errorTab = resolveErrorTab(errors);
    if (errorTab) {
      setActiveTab(errorTab);
    }
  }, []);

  // Handle form submission from edit tab
  const handleFormSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const result = await submitForm();
      if (!result.success) {
        focusErrorTab(result.errors);
      }
    },
    [submitForm, focusErrorTab],
  );

  // Handle config tab save and switch to edit
  const handleConfigSave = useCallback(async () => {
    const result = await submitForm();
    if (result.success) {
      setActiveTab(TabTypes.edit);
    } else {
      focusErrorTab(result.errors);
    }
  }, [submitForm, focusErrorTab]);

  // Filter visible workout groups (not marked for destruction)
  const visibleWorkoutGroups = workoutGroupsField.fields.filter(
    (field, index) => {
      const group = form.getValues(`workoutGroups.${index}`);
      return !group?._destroy;
    },
  );

  return (
    <FormProvider {...form}>
      <div>
        <div className="row">
          <div className="header">
            <Breadcrumb path={path} />
          </div>
        </div>

        <TabsHeader
          tabs={TabsList}
          onPress={onChangeTab}
          tabActive={activeTab}
          errors={errors}
        />

        <RoutineFormContainer>
          {isLoading && (
            <LoadingComponent loading={isLoading} progress={progress} />
          )}

          {activeTab === 'config' ? (
            <ConfigTab
              form={form}
              daysWeek={daysWeek}
              users={users}
              programs={programs}
              isLoading={isLoading}
              onSave={handleConfigSave}
            />
          ) : (
            <form className="form-workout-groups" onSubmit={handleFormSubmit}>
              <WorkoutGroupsContainer>
                {workoutGroupsField.fields.map((field, index) => {
                  const group = form.getValues(`workoutGroups.${index}`);
                  // Skip destroyed groups
                  if (group?._destroy) return null;

                  const visibleIndex = visibleWorkoutGroups.findIndex(
                    (g) => g.fieldId === field.fieldId,
                  );

                  return (
                    <WorkoutGroupField
                      key={field.fieldId}
                      form={form}
                      groupIndex={index}
                      onRemove={() => removeWorkoutGroup(index)}
                      onMoveUp={
                        visibleIndex > 0
                          ? () => moveWorkoutGroup(index, index - 1)
                          : undefined
                      }
                      onMoveDown={
                        visibleIndex < visibleWorkoutGroups.length - 1
                          ? () => moveWorkoutGroup(index, index + 1)
                          : undefined
                      }
                      isFirst={visibleIndex === 0}
                      isLast={visibleIndex === visibleWorkoutGroups.length - 1}
                      exerciseListDefault={exerciseListDefault}
                    />
                  );
                })}

                <AddWorkoutGroupButton onClick={() => addWorkoutGroup()}>
                  <i className="fas fa-plus" style={{marginRight: '8px'}} />
                  Adicionar novo treino
                </AddWorkoutGroupButton>
              </WorkoutGroupsContainer>

              <SubmitButtonContainer>
                <div className="row">
                  <div className="col s12">
                    <button
                      className="btn btn-primary"
                      type="submit"
                      disabled={isLoading}>
                      {isLoading ? 'Salvando...' : 'Publicar alterações'}
                    </button>
                  </div>
                </div>
              </SubmitButtonContainer>
            </form>
          )}
        </RoutineFormContainer>
      </div>
    </FormProvider>
  );
});

export default WorkoutRoutineForm;
